<template>
  <div>
    <swiper
      :modules="modules"
      class="main-slider"
      effect="fade"
      :pagination="pagination"
      :autoplay="autoplay"
      :loop="true"
    >
      <swiper-slide
        v-for="(promotion, index) in promotions"
        :key="promotion.uuid"
      >
        <NuxtLinkLocale
          :to="promotion.link"
          :herf="promotion.link"
          :external="isExternalLink(promotion)"
        >
          <SliderItem :promotion="promotion" :index="index" />
        </NuxtLinkLocale>
      </swiper-slide>
    </swiper>

    <div
      v-show="promotions.length"
      ref="paginationEl"
      class="main-slider__pagination z-index__1"
    />
  </div>
</template>

<script setup lang="ts">
import { Swiper, SwiperSlide } from 'swiper/vue'
import { EffectFade, Pagination, Keyboard, Autoplay } from 'swiper/modules'
import type { PromotionsSlide } from '~/types/promotion'

const modules = [Pagination, EffectFade, Keyboard, Autoplay]

type Props = {
  promotions: PromotionsSlide[]
}
defineProps<Props>()

const paginationEl = useTemplateRef<HTMLDivElement>('paginationEl')

const pagination = computed(() => ({
  el: paginationEl.value!,
  clickable: true,
  bulletClass: 'main-slider__pagination-bullet',
  bulletActiveClass: 'main-slider__pagination-bullet--active'
}))
const autoplay = {
  delay: 2500,
  disableOnInteraction: false
}

const isExternalLink = (promotion: PromotionsSlide) => {
  return promotion.link.substring(0, 4) === 'http'
}

</script>

<style lang="scss">
.main-slider {
  height: calc(57.5vw - 1.6rem);

  @media only screen and (min-width: 740px) {
    height: calc(35.6vw - 2rem);
  }
  @media only screen and (min-width: 1024px) {
    height: 34rem;
  }
  @media only screen and (min-width: 1300px) {
    height: 43.5rem;
  }

  .swiper-slide {
    background-color: #FFFFFF;
    @media only screen and (max-width: 1024px) {
      height: auto;
    }
  }

  &__prev,
  &__next {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    color: $white-color;
    cursor: pointer;
    padding: 2rem;
    opacity: 0;
    transition: opacity 200ms, color 200ms;
    &:hover {
      color: $lighter-gray-color;
    }
  }
  &:hover {
    .main-slider__prev,
    .main-slider__next {
      opacity: 1;
    }
  }

  &__prev {
    left: 2rem;
  }

  &__next {
    right: 2rem;
  }

  &__pagination {
    --swiper-pagination-bottom: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 0rem;
    min-height: 2.4rem;

    @media only screen and (max-width: 1024px) {
      display: none;
    }
  }

  &__pagination-bullet {
    display: inline-block;
    width: 2.4rem;
    height: 2.4rem;
    cursor: pointer;
    margin: 0 0.5rem;
    background-color: transparent;
    transition: all ease 200ms;
    outline: none;
    position: relative;

    &:before {
      position: absolute;
      top: 1.1rem;
      left: 0;
      right: 0;
      height: 0.2rem;
      width: 100%;
      background-color: #e7e7e7;
      content: '';
    }

    &:not(&--active):hover {
      &:before {
        background-color: #a2a2a2;
      }
    }

    &--active {
      &:before {
        background-color: #ffca00;
      }

      width: 4.8rem;
    }
  }
}
</style>
